import * as c from './types';

import {
  GetRealTimeDataParams,
  IRealTimeService
} from '_clean-code/services/contracts/realtime.service';
import { Metrics } from '_clean-code/types/realtime';

/* eslint-disable import/prefer-default-export */
import AppServices from '_clean-code/services';
import { I18n } from '@utils/i18n';
import { IStopsService } from '_clean-code/services/contracts/stops.service';

import { EMachineListsVariants, MachineItem } from './reducers';
import { MachineOption } from 'types';
import ServiceContext from '_clean-code/services/context/service.context';
import createServiceAction from '_clean-code/services/stateManager/createServiceAction';
import toastr from '@utils/toastr';
import { sortBy } from 'lodash';

export const fetchRealtimeMachineData = (
  params: GetRealTimeDataParams,
  isTvMode: boolean
) =>
  createServiceAction({
    doAction: c.FETCH_REALTIME_DATA,
    successAction: c.FETCH_REALTIME_DATA_SUCCESS,
    errorAction: c.FETCH_REALTIME_DATA_ERROR,
    do: async () => {
      const realTimeService = ServiceContext.resolve<IRealTimeService>(
        AppServices.RealTimeService
      );
      let metricsData: Metrics[] = [];
      if (isTvMode) {
        metricsData = await realTimeService.getMachineMetrics({
          machineIds: params.machineIds,
          noOfDays: params.noOfDays
        });
      }
      const realtimeMachinesData =
        await realTimeService.getRealTimeDataForMachines(params, metricsData);
      return realtimeMachinesData;
    },
    errorHandler({ response }) {
      if (!response || !response.data) {
        toastr.error(
          I18n.t('system_popup'),
          I18n.t('error_getting_real_time_machine_data')
        );
        return;
      }
      const { error } = response.data;
      toastr.error(I18n.t('system_popup'), error);
    }
  });

export const fetchSortedMachineList = (
  metric: string,
  sortType: 'asc' | 'desc',
  allMachines: (MachineOption & {
    machineGroupId: string;
  })[],
  variant?: EMachineListsVariants
) =>
  createServiceAction({
    doAction: c.FETCH_SORTED_MACHINE_LIST,
    successAction: c.FETCH_SORTED_MACHINE_LIST_SUCCESS,
    errorAction: c.FETCH_SORTED_MACHINE_LIST_ERROR,
    doActionPayload: { metricType: metric, sortType, variant },
    do: async () => {
      if (metric === 'machine_title') {
        const sortedMachines = sortMachines(allMachines, sortType);
        return {
          machinesIds: sortedMachines.map((e) => e.value),
          metricType: metric,
          sortType,
          variant: variant ?? EMachineListsVariants.Default
        };
      }
      if (metric === 'machine_order') {
        let sortedMachines = sortBy(allMachines, 'order');
        if (sortType === 'desc') {
          sortedMachines = sortedMachines.reverse();
        }
        return {
          machinesIds: sortedMachines.map((e) => e.value),
          metricType: metric,
          sortType,
          variant: variant ?? EMachineListsVariants.Default
        };
      }
      const realTimeService = ServiceContext.resolve<IRealTimeService>(
        AppServices.RealTimeService
      );
      const sortedMachineList = await realTimeService.getSortedMachineList({
        metric,
        sortType,
        machineId: allMachines.map((e) => e.value)
      });
      return {
        ...sortedMachineList,
        metricType: metric,
        sortType,
        variant: variant ?? EMachineListsVariants.Default
      };
    },
    errorHandler({ response }) {
      if (!response || !response.data) {
        toastr.error(
          I18n.t('system_popup'),
          I18n.t('error_getting_machine_list')
        );
        return;
      }
      const { error } = response.data;
      toastr.error(I18n.t('system_popup'), error);
    }
  });

export const fetchStopsDataByShift = (shiftId: number) =>
  createServiceAction({
    doAction: c.FETCH_STOP_DATA_BY_SHIFT,
    successAction: c.FETCH_STOP_DATA_BY_SHIFT_SUCCESS,
    errorAction: c.FETCH_STOP_DATA_BY_SHIFT_ERROR,
    do: async () => {
      const stopsService = ServiceContext.resolve<IStopsService>(
        AppServices.StopsService
      );
      const stops = await stopsService.getStopsByShift(shiftId);
      return { stops, shiftId };
    },
    errorHandler({ response }) {
      if (!response || !response.data) {
        toastr.error(
          I18n.t('system_popup'),
          I18n.t('error_stops_data_by_shift')
        );
        return;
      }
      const { error } = response.data;
      toastr.error(I18n.t('system_popup'), error);
    }
  });

export const addMachinesToDisplayList = (machines: MachineItem[]) => ({
  type: c.ADD_MACHINES_TO_DISPLAY_LIST,
  payload: machines
});

export const resetMachinesToDisplayList = () => ({
  type: c.RESET_MACHINES_TO_DISPLAY_LIST
});

export const emptyMachineData = () => ({
  type: c.EMPTY_MACHINE_DATA
});

export const loadMachinesSortingFilter = (
  companyId: string | null,
  variant: EMachineListsVariants
) => ({
  type: c.LOAD_MACHINES_SORTING_FILTER,
  payload: { companyId, variant }
});

const sortMachines = (machines: MachineOption[], sortType: 'asc' | 'desc') => {
  const reA = /[^a-zA-Z]/g;
  const reN = /[^0-9]/g;
  return machines.sort((a, b) => {
    const aA = a.label.replace(reA, '');
    const bA = b.label.replace(reA, '');
    if (aA === bA) {
      const aN = parseInt(a.label.replace(reN, ''), 10);
      const bN = parseInt(b.label.replace(reN, ''), 10);
      // eslint-disable-next-line no-nested-ternary
      if (sortType === 'asc') {
        return aN === bN ? 0 : aN > bN ? 1 : -1;
      }
      return aN === bN ? 0 : aN > bN ? -1 : 1;
    }
    if (sortType === 'asc') {
      return aA > bA ? 1 : -1;
    }
    return aA > bA ? -1 : 1;
  });
};
